/**
 * ErrorBoundary Component
 *
 * Functional component wrapper around class-based error boundary.
 * Catches JavaScript errors in child components and displays fallback UI.
 * Prevents entire application crashes in production.
 */
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Container, Typography, Paper } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { gradients } from "../utils/theme";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback UI component */
  fallback?: ReactNode;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorFallbackProps {
  error: Error;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}

/**
 * Error Fallback UI - Functional component for displaying errors
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
}) => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            maxWidth: 500,
          }}
        >
          <ErrorOutline
            sx={{
              fontSize: 64,
              color: "error.main",
              mb: 2,
            }}
          />
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Something Went Wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We apologize for the inconvenience. The application encountered an
            unexpected error.
          </Typography>

          {process.env.NODE_ENV === "development" && error && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                textAlign: "left",
                bgcolor: "grey.50",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {error.toString()}
                {errorInfo?.componentStack}
              </Typography>
            </Paper>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onReset}
            >
              Try Again
            </Button>
            <Button
              variant="contained"
              onClick={handleReload}
              sx={{
                background: gradients.navyLux,
              }}
            >
              Reload Page
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundaryClass - Class component required for error boundary
 * (React doesn't support error boundaries in functional components yet)
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Call optional error callback (for error tracking services)
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use functional error fallback component
      return (
        <ErrorFallback
          error={this.state.error!}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorBoundary - Functional wrapper for ErrorBoundaryClass
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback,
  onError,
}) => {
  return (
    <ErrorBoundaryClass fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundary;
